class HeaderElement extends HTMLElement {
  connectedCallback () {
    this.levelStack = ['root']
    this.setupDesktopMenu()
    this.setupMobileMenu()
    this.setupSearchModal()
    this.setupMobileLevels()
    this.setupMobileMenuSearch()
    this.initializeMobileMenuPositioning()
  }

  setupDesktopMenu () {
    this.menuDesktopParents.forEach(menuDesktopParent => {
      const menuDrawer = menuDesktopParent.querySelector('[menu-drawer]')
      if (!menuDrawer) return
      menuDesktopParent.addEventListener('mouseover', () => {
        menuDrawer.classList.remove('invisible', 'opacity-0')
        this.toggleSearchModal(false)
        window.dispatchEvent(new CustomEvent('show:body-obscure'))
      })

      menuDesktopParent.addEventListener('mouseout', () => {
        menuDrawer.classList.add('invisible', 'opacity-0')
        window.dispatchEvent(new CustomEvent('hide:body-obscure'))
      })
    })
  }

  setupMobileMenu () {
    if (this.openMobileMenuTrigger) {
      this.openMobileMenuTrigger.addEventListener('click', () => {
        this.toggleMobileMenu(true)
      })
    }

    // Handle all elements with close-menu-mobile attribute
    const closeTriggers = this.querySelectorAll('[close-menu-mobile]')
    closeTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        // Don't immediately close for chat triggers - let chat component handle it
        if (trigger.classList.contains('chat__trigger')) {
          console.log('Header: Chat trigger clicked, NOT closing menu immediately')
          return
        }
        console.log('Header: Non-chat close trigger clicked, closing menu')
        this.toggleMobileMenu(false)
      })
    })

    window.addEventListener('show:mobile-menu', () => {
      this.toggleMobileMenu(true)
    })

    window.addEventListener('hide:mobile-menu', () => {
      console.log('Header: Received hide:mobile-menu event')
      this.toggleMobileMenu(false)
    })
  }

  setupSearchModal () {
    if (this.openSearchModal) {
      this.openSearchModal.addEventListener('click', () => {
        this.toggleSearchModal(true)
      })
    }

    if (this.closeSearchModal) {
      this.closeSearchModal.addEventListener('click', () => {
        this.toggleSearchModal(false)
      })
    }

    if (this.cancelSearchModal) {
      this.cancelSearchModal.addEventListener('click', () => {
        this.toggleSearchModal(false)
      })
    }

    window.addEventListener('show:search-modal', () => {
      this.toggleSearchModal(true)
    })

    window.addEventListener('hide:search-modal', () => {
      this.toggleSearchModal(false)
    })
  }

  setupMobileLevels () {
    if (!this.mobileNav) return

    this.flattenMobileLevels()
    this.resetLevelsToRoot()
    this.setupMobileLevelFaders()

    this.mobileNav.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-open-level]')
      if (openBtn) {
        const id = openBtn.getAttribute('data-open-level')
        this.openLevel(id)
        return
      }

      const backBtn = e.target.closest('[data-back]')
      if (backBtn) {
        this.goBackOneLevel(backBtn)
      }

      // Only act on anchor links that navigate away (not level openers)
      const linkEl = e.target.closest('a[href]')
      if (linkEl && !linkEl.hasAttribute('data-open-level')) {
        const href = linkEl.getAttribute('href') || ''
        // ignore hash-only navigation within menu
        if (href && !href.startsWith('#')) {
          this.showMobileMenuLoadingSpinner()
        }
      }
    })
  }

  setupMobileMenuSearch () {
    const nav = this.mobileNav
    if (!nav) return
    const boxes = nav.querySelectorAll('.mobile-menu__search-box')
    boxes.forEach(box => this.bindMobileSearchBox(box))
  }

  bindMobileSearchBox (box) {
    const input = box.querySelector('.mobile-menu__search-input')
    const label = box.querySelector('.mobile-menu__floating-label')
    const list = box.querySelector('.mobile-menu__search-suggestions')
    if (!input || !label || !list) return

    let cachedItems = []
    let inactivityTimer = null
    let suppressBlurClose = false
    let resultsShown = false

    const activateLabel = () => {
      label.classList.remove('top-1/2', '-translate-y-1/2', 'text-base')
      label.classList.add('top-2', 'text-sm')
      const ph = input.getAttribute('data-placeholder') || label.textContent.trim()
      input.setAttribute('data-placeholder', ph)
      input.placeholder = ph
    }

    const deactivateLabel = () => {
      if (input.value.trim() !== '') return
      label.classList.remove('top-2', 'text-sm')
      label.classList.add('top-1/2', '-translate-y-1/2')
      input.placeholder = ''
    }

    const hideSuggestions = () => {
      list.classList.add('hidden')
    }

    const showSuggestions = () => {
      list.classList.remove('hidden')
    }

    const debounce = (fn, wait) => {
      let t
      return (...args) => {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, args), wait)
      }
    }

    const ensureListFader = () => {
      let hasItems = false
      for (const child of Array.from(list.children)) {
        if (!child.classList.contains('mobile-menu__search-fader')) { hasItems = true; break }
      }
      if (!hasItems) {
        const existing = list.querySelector('.mobile-menu__search-fader')
        if (existing) existing.remove()
        return null
      }
      let f = list.querySelector('.mobile-menu__search-fader')
      if (!f) {
        f = document.createElement('li')
        f.className = 'mobile-menu__search-fader pointer-events-none absolute bottom-0 left-0 right-0 h-16 z-20 bg-gradient-to-t from-white to-transparent opacity-0 transition-opacity duration-150'
        f.setAttribute('aria-hidden', 'true')
        f.innerHTML = '<span class="sr-only">Scroll down to see more results</span>'
        list.appendChild(f)
      }
      return f
    }

    const updateListFader = () => {
      const f = ensureListFader()
      if (!f) return
      const sh = list.scrollHeight || 0
      const ch = list.clientHeight || 0
      const st = list.scrollTop || 0

      // Compute scrollability EXCLUDING the fader height so the fader doesn't create fake overflow
      const fh = f.offsetHeight || 0
      const contentScrollHeight = Math.max(0, sh - fh)
      const can = contentScrollHeight > ch + 1
      const atEnd = st + ch >= contentScrollHeight - 1

      // If there is nothing to scroll, remove the fader completely
      if (!can) {
        f.remove()
        return
      }

      // Otherwise, show/hide based on scroll position
      f.classList.remove('hidden')
      if (!atEnd) {
        f.classList.remove('opacity-0')
        f.classList.add('opacity-100')
      } else {
        f.classList.remove('opacity-100')
        f.classList.add('opacity-0')
      }
    }

    const renderResults = (items) => {
      list.innerHTML = ''
      if (!items.length) {
        const li = document.createElement('li')
        li.className = 'px-3.5 py-2.5 text-gray-500 cursor-default'
        li.textContent = box.getAttribute('data-no-results-text') || 'No results found'
        list.appendChild(li)
        showSuggestions()
        resultsShown = true
        // No fader for empty state
        return
      }
      items.slice(0, 6).forEach((item, index) => {
        const li = document.createElement('li')
        li.className = 'border-b border-clay last:border-b-0'
        const a = document.createElement('a')
        a.href = item.href
        a.className = 'block w-full'
        a.setAttribute('tabindex', '-1')

        const row = document.createElement('div')
        row.className = 'flex items-center gap-4 py-3 px-3 hover:bg-oat focus:outline-none'

        if (item.image) {
          const imgWrap = document.createElement('div')
          imgWrap.className = 'rounded bg-oat aspect-[4/3] w-16 h-auto overflow-hidden flex items-center justify-center'
          imgWrap.innerHTML = item.image
          row.appendChild(imgWrap)
        } else {
          const ph = document.createElement('div')
          ph.className = 'rounded bg-oat aspect-[4/3] w-16 h-auto flex items-center justify-center'
          const sr = document.createElement('span')
          sr.className = 'sr-only'
          sr.textContent = 'No image available'
          ph.appendChild(sr)
          row.appendChild(ph)
        }

        const content = document.createElement('div')
        content.className = 'flex-1 min-w-0'
        const title = document.createElement('div')
        title.className = 'text-base font-medium truncate'
        title.textContent = item.title
        content.appendChild(title)
        if (item.priceHtml) {
          const price = document.createElement('div')
          price.className = 'text-sm'
          price.innerHTML = item.priceHtml
          content.appendChild(price)
        }
        row.appendChild(content)
        a.appendChild(row)
        li.appendChild(a)
        list.appendChild(li)
      })
      // Show before measuring heights
      showSuggestions()

      // Add bottom spacing only when the list is actually scrollable
      const nonFaderItems = Array.from(list.querySelectorAll('li:not(.mobile-menu__search-fader)'))
      nonFaderItems.forEach(item => { item.style.marginBottom = '' })
      const fader = list.querySelector('.mobile-menu__search-fader')
      const fh = fader ? (fader.offsetHeight || 0) : 0
      const canScroll = (list.scrollHeight - fh) > (list.clientHeight || 0) + 1
      if (canScroll && fh > 0) {
        // Provide breathing room so last item can scroll fully above the fader overlay
        list.style.paddingBottom = `${fh}px`
      } else {
        list.style.paddingBottom = '0px'
      }

      resultsShown = true
      ensureListFader(); updateListFader()

      // When the list is not scrollable, prevent tiny scroll areas by clamping max-height to content height
      if (!canScroll) {
        list.style.maxHeight = 'none'
      } else {
        list.style.maxHeight = ''
      }
    }

    const parsePredictiveHtml = (htmlText) => {
      const doc = new DOMParser().parseFromString(htmlText, 'text/html')
      const container = doc.querySelector('#shopify-section-predictive-search')
      if (!container) return []
      const anchors = container.querySelectorAll('.predictive-search__result-item a')
      const items = []
      anchors.forEach(a => {
        const href = a.getAttribute('href') || '#'
        const titleEl = a.querySelector('.predictive-search__result-title')
        const priceEl = a.querySelector('.predictive-search__result-price')
        const imgEl = a.querySelector('img')
        items.push({
          href,
          title: titleEl ? titleEl.textContent.trim() : a.textContent.trim(),
          priceHtml: priceEl ? priceEl.innerHTML : '',
          image: imgEl ? imgEl.outerHTML : '',
        })
      })
      return items
    }

    const fetchResults = async (term) => {
      const basePath = (window?.Shopify?.routes?.root || '/')
      const url = `${basePath}search/suggest?q=${encodeURIComponent(term)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=6&section_id=predictive-search`
      const res = await fetch(url)
      if (!res.ok) return []
      const text = await res.text()
      return parsePredictiveHtml(text)
    }

    const handleQuery = async (val) => {
      const q = (val || '').trim()
      if (q.length < 2) {
        hideSuggestions()
        resultsShown = false
        return
      }
      try {
        list.innerHTML = ''
        const loading = document.createElement('li')
        loading.className = 'px-3.5 py-2.5 flex items-center justify-center'
        loading.innerHTML = '<div class="h-5 w-5 text-black animate-spin" role="status" aria-live="polite"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg><span class="sr-only">Loading...</span></div>'
        list.appendChild(loading)
        showSuggestions()
        const items = await fetchResults(q)
        cachedItems = items
        renderResults(items)
      } catch (e) {
        hideSuggestions()
        resultsShown = false
      }
    }

    const startInactivityTimer = () => {
      clearTimeout(inactivityTimer)
      if (!resultsShown) return
      // On iOS Safari, ensure blur is triggered even if soft keyboard keeps focus
      inactivityTimer = setTimeout(() => {
        try {
          suppressBlurClose = true
          input.setAttribute('readonly', 'true')
          input.blur()
          setTimeout(() => {
            input.removeAttribute('readonly')
            suppressBlurClose = false
          }, 180)
        } catch (_) {
          suppressBlurClose = false
        }
      }, 2000)
    }

    input.addEventListener('focus', () => {
      activateLabel()
      if (input.value.trim().length >= 2 && cachedItems.length) {
        renderResults(cachedItems)
      }
      showSuggestions()
      startInactivityTimer()
      try {
        const container = box.closest('.mobile-menu__level')
        if (container) {
          const cRect = container.getBoundingClientRect()
          const bRect = box.getBoundingClientRect()
          let stickyOffset = 0
          const stickyBack = container.querySelector('.mobile-menu__back')
          if (stickyBack) {
            const sbRect = stickyBack.getBoundingClientRect()
            stickyOffset = (sbRect.height || 60) + 5
          }
          const delta = bRect.top - cRect.top - stickyOffset - 8
          const targetTop = Math.max(0, container.scrollTop + delta)
          container.scrollTo({ top: targetTop, behavior: 'smooth' })
        }
      } catch (_) {}
    })
    input.addEventListener('blur', () => {
      setTimeout(() => {
        if (!suppressBlurClose && !input.value.trim()) {
          deactivateLabel()
        }
      }, 100)
    })
    input.addEventListener('input', debounce((e) => {
      handleQuery(e.target.value)
      startInactivityTimer()
    }, 250))

    const outsideClickHandler = (evt) => {
      if (!box.contains(evt.target)) {
        hideSuggestions()
        resultsShown = false
      }
    }
    document.addEventListener('click', outsideClickHandler, true)
    document.addEventListener('touchend', outsideClickHandler, true)
    if (!list._faderScrollBound) {
      list.addEventListener('scroll', updateListFader)
      list._faderScrollBound = true
    }
  }

  openLevel (levelId) {
    const currentId = this.levelStack[this.levelStack.length - 1]
    const currentLevel = this.getLevel(currentId)
    const nextLevel = this.getLevel(levelId)
    if (!currentLevel || !nextLevel) return

    currentLevel.classList.remove('translate-x-0')
    currentLevel.classList.add('-translate-x-full')

    nextLevel.classList.remove('translate-x-full')
    nextLevel.classList.add('translate-x-0')
    nextLevel.classList.add('z-10')
    currentLevel.classList.remove('z-10')

    // Reset scroll position to top when navigating to a new level
    nextLevel.scrollTop = 0

    this.levelStack.push(levelId)
    this.updateLevelFader(nextLevel)
    this.updateFooterContactButtonForCurrentLevel()
  }

  goBackOneLevel (backBtn) {
    if (this.levelStack.length <= 1) return
    const currentId = this.levelStack.pop()
    const prevId = this.levelStack[this.levelStack.length - 1]

    const currentLevel = this.getLevel(currentId)
    const prevLevel = this.getLevel(prevId)
    if (!currentLevel || !prevLevel) return

    currentLevel.classList.remove('translate-x-0')
    currentLevel.classList.add('translate-x-full')

    prevLevel.classList.remove('-translate-x-full')
    prevLevel.classList.add('translate-x-0')
    prevLevel.classList.add('z-10')
    currentLevel.classList.remove('z-10')

    // Reset scroll position to top when navigating back to a previous level
    prevLevel.scrollTop = 0

    this.updateLevelFader(prevLevel)
    this.updateFooterContactButtonForCurrentLevel()
  }

  toggleMobileMenu (val) {
    console.log('Header: toggleMobileMenu called with val:', val)
    const chatBundle = document.querySelector('.chat-bundle')

    if (val) {
      this.mobileMenu.classList.remove('-translate-x-[102%]')
      this.openMobileMenuTrigger.classList.add('tb-hidden')
      this.closeMobileMenuTrigger.classList.remove('tb-hidden')
      const closeBox = this.closeMobileMenuTrigger.querySelector('div')
      if (closeBox) closeBox.classList.add('bg-oat', 'rounded-md', 'text-orange')
      this.toggleSearchModal(false)
      window.dispatchEvent(new CustomEvent('prevent:page-scroll'))
      window.dispatchEvent(new CustomEvent('show:body-obscure'))
      if (chatBundle) chatBundle.style.zIndex = '10'
      document.body.classList.add('tb-overflow-hidden')
      try {
        this._lockedScrollY = window.scrollY || document.documentElement.scrollTop || 0
        document.body.style.position = 'fixed'
        document.body.style.top = `-${this._lockedScrollY}px`
        document.body.style.left = '0'
        document.body.style.right = '0'
        document.body.style.width = '100%'
      } catch (_) {}
      this.updateAllLevelFaders()
      this.updateFooterContactButtonForCurrentLevel()
      return
    }

    this.mobileMenu.classList.add('-translate-x-[102%]')
    this.openMobileMenuTrigger.classList.remove('tb-hidden')
    this.closeMobileMenuTrigger.classList.add('tb-hidden')
    const closeBox = this.closeMobileMenuTrigger.querySelector('div')
    if (closeBox) closeBox.classList.remove('bg-oat', 'rounded-md', 'text-orange')
    // Reset levels back to root when closing
    this.resetLevelsToRoot()
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    window.dispatchEvent(new CustomEvent('hide:body-obscure'))
    if (chatBundle) chatBundle.style.zIndex = '70'
    document.body.classList.remove('tb-overflow-hidden')
    try {
      const lockedY = this._lockedScrollY || 0
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      if (lockedY) window.scrollTo(0, lockedY)
      this._lockedScrollY = 0
    } catch (_) {}

    // Reset spinner state when menu closes
    this.hideMobileMenuLoadingSpinner()
  }

  resetLevelsToRoot () {
    if (!this.mobileNav) return
    const allLevels = this.mobileNav.querySelectorAll('.mobile-menu__level')
    allLevels.forEach(level => {
      const id = level.getAttribute('data-level-id')
      if (id === 'root') {
        level.classList.add('translate-x-0')
        level.classList.remove('translate-x-full', '-translate-x-full')
        level.classList.add('z-10')
        // Reset scroll position to top when returning to root
        level.scrollTop = 0
      } else {
        level.classList.add('translate-x-full')
        level.classList.remove('translate-x-0', '-translate-x-full', 'z-10')
      }
    })
    this.levelStack = ['root']
    const root = this.getLevel('root')
    if (root) this.updateLevelFader(root)
    this.updateFooterContactButtonForCurrentLevel()
  }

  toggleSearchModal (val) {
    if (!this.searchModal || !this.openSearchModal || !this.closeSearchModal) return
    if (val) {
      this.searchModal.classList.remove('tb-hidden')
      this.openSearchModal.classList.add('tb-hidden')
      this.closeSearchModal.classList.remove('tb-hidden')
      this.toggleMobileMenu(false)
      window.dispatchEvent(new CustomEvent('show:body-obscure'))
      if (this.searchInput) this.searchInput.focus()
      return
    }

    this.searchModal.classList.add('tb-hidden')
    this.openSearchModal.classList.remove('tb-hidden')
    this.closeSearchModal.classList.add('tb-hidden')
    window.dispatchEvent(new CustomEvent('hide:body-obscure'))
  }

  getLevel (id) {
    if (!this.mobileNav) return null
    const levels = this.mobileNav.querySelectorAll('.mobile-menu__level')
    for (const el of levels) {
      if (el.getAttribute('data-level-id') === id) return el
    }
    return null
  }

  setupMobileLevelFaders () {
    if (!this.mobileNav) return
    const levels = this.mobileNav.querySelectorAll('.mobile-menu__level')
    levels.forEach(level => this.ensureLevelFader(level))
    levels.forEach(level => {
      if (!level._faderScrollBound) {
        level.addEventListener('scroll', () => this.updateLevelFader(level))
        level._faderScrollBound = true
      }
      this.updateLevelFader(level)
    })
    if (!this._faderResizeBound) {
      window.addEventListener('resize', () => this.updateAllLevelFaders())
      this._faderResizeBound = true
    }
  }

  ensureLevelFader (level) {
    if (!level) return null
    let fader = level.querySelector('.mobile-menu__fader')
    if (!fader) {
      // Persistently rendered in Liquid; fallback only if missing
      fader = document.createElement('div')
      fader.className = 'mobile-menu__fader pointer-events-none sticky bottom-0 left-0 right-0 h-10 z-20 bg-gradient-to-t from-white to-transparent opacity-0 transition-opacity duration-150'
      fader.setAttribute('aria-hidden', 'true')
      fader.innerHTML = '<span class="sr-only">Scroll down to see more</span>'
      level.appendChild(fader)
    }
    return fader
  }

  updateLevelFader (level) {
    if (!level) return
    const fader = this.ensureLevelFader(level)
    const scrollHeight = level.scrollHeight || 0
    const clientHeight = level.clientHeight || 0
    const scrollTop = level.scrollTop || 0
    const canScroll = scrollHeight > clientHeight + 1
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1
    if (canScroll && !atBottom) {
      fader.classList.remove('opacity-0')
      fader.classList.add('opacity-100')
    } else {
      fader.classList.remove('opacity-100')
      fader.classList.add('opacity-0')
    }
  }

  updateAllLevelFaders () {
    if (!this.mobileNav) return
    const levels = this.mobileNav.querySelectorAll('.mobile-menu__level')
    levels.forEach(level => this.updateLevelFader(level))
  }

  updateFooterContactButtonForCurrentLevel () {
    const greenBtn = this.greenContactButton
    const defaultBtn = this.defaultContactButton
    if (!greenBtn || !defaultBtn) return
    const currentId = this.levelStack[this.levelStack.length - 1]
    const level = this.getLevel(currentId)
    const isGreen = !!(level && level.hasAttribute('data-root-green'))
    if (isGreen) {
      defaultBtn.classList.add('hidden')
      greenBtn.classList.remove('hidden')
    } else {
      greenBtn.classList.add('hidden')
      defaultBtn.classList.remove('hidden')
    }
  }

  get menuDesktopParents () {
    return this.querySelectorAll('[menu-parent]')
  }

  initializeMobileMenuPositioning () {
    this.updateMobileMenuPosition()

    // Update on scroll and resize
    let ticking = false
    const updatePosition = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateMobileMenuPosition()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
  }

  calculateTotalHeaderHeight () {
    let totalHeight = 0

    // Get announcement bar height only if it's visible in viewport
    const announcementBar = document.querySelector('.announcement-bar')
    if (announcementBar) {
      const rect = announcementBar.getBoundingClientRect()
      // Only count height if the element is visible at the top (not scrolled past)
      if (rect.top >= -5 && rect.bottom > 0) { // Small buffer for precision
        totalHeight += rect.height
      }
    }

    // Get main header height (always visible due to fixed/sticky positioning)
    const siteHeader = document.querySelector('.site-header')
    if (siteHeader) {
      const rect = siteHeader.getBoundingClientRect()
      totalHeight += rect.height
    }

    // Promo bar height is ignored for mobile menu positioning
    return totalHeight
  }

  updateMobileMenuPosition () {
    const totalHeaderHeight = this.calculateTotalHeaderHeight()

    // Set CSS custom property for total header height to use in height calculation
    document.documentElement.style.setProperty('--total-header-height', `${totalHeaderHeight}px`)
  }

  get mobileMenu () {
    return this.querySelector('[mobile-menu]')
  }

  get mobileNav () {
    return this.querySelector('[data-mobile-nav]')
  }

  get openMobileMenuTrigger () {
    return this.querySelector('[open-menu-mobile]')
  }

  get closeMobileMenuTrigger () {
    return this.querySelector('[close-menu-mobile]')
  }

  get searchModal () {
    return this.querySelector('[search-modal]')
  }

  get openSearchModal () {
    return this.querySelector('[open-search-modal]')
  }

  get closeSearchModal () {
    return this.querySelector('[close-search-modal]')
  }

  get cancelSearchModal () {
    return this.querySelector('[cancel-search-modal]')
  }

  get searchInput () {
    return this.querySelector('[search-input]')
  }

  get defaultContactButton () {
    return this.querySelector('.mobile-menu__footer-contact-button')
  }

  get greenContactButton () {
    return this.querySelector('.mobile-menu__footer-contact-button--green')
  }

  flattenMobileLevels () {
    const levels = this.mobileNav ? this.mobileNav.querySelectorAll('.mobile-menu__level') : []
    levels.forEach(level => {
      if (level.parentElement !== this.mobileNav) {
        this.mobileNav.appendChild(level)
      }
    })
  }

  showMobileMenuLoadingSpinner () {
    const closeBtn = this.closeMobileMenuTrigger
    if (!closeBtn) return
    const closeIcon = closeBtn.querySelector('.site-header__menu-mobile-close-icon')
    const spinner = closeBtn.querySelector('.loading-overlay__spinner')
    if (closeIcon) closeIcon.classList.add('opacity-0')
    if (spinner) spinner.classList.remove('opacity-0')
  }

  hideMobileMenuLoadingSpinner () {
    const closeBtn = this.closeMobileMenuTrigger
    if (!closeBtn) return
    const closeIcon = closeBtn.querySelector('.site-header__menu-mobile-close-icon')
    const spinner = closeBtn.querySelector('.loading-overlay__spinner')
    if (spinner) spinner.classList.add('opacity-0')
    if (closeIcon) closeIcon.classList.remove('opacity-0')
  }
}

if (!customElements.get('header-element')) {
  customElements.define('header-element', HeaderElement)
}
